import { useEffect, useRef, useState } from "react";
import { DatabaseBackup, KeyRound, Loader2, Mail, Save, Upload, Lock, AlertTriangle, Trash2 } from "lucide-react";
import { api, fileUrl, getErrorMessage } from "../../api";
import { useNurserySettings } from "../../SettingsContext";
import PageHeader from "../../components/PageHeader";
import ConfirmDialog from "../../components/ConfirmDialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { Badge } from "../../components/ui/badge";

export default function Settings() {
  const [tab, setTab] = useState("general");
  const [s, setS] = useState(null);
  const [tpls, setTpls] = useState([]);

  const reload = async () => {
    const [set, tpl] = await Promise.all([api.get("/admin/settings"), api.get("/admin/email-templates")]);
    setS(set.data); setTpls(tpl.data);
  };
  useEffect(() => { reload(); }, []);

  if (!s) return <div className="p-6 text-[#5C665A]" data-testid="settings-loading">Loading…</div>;

  return (
    <>
      <PageHeader title="Settings" subtitle="Nursery info, email delivery, and templates." testid="page-settings" />
      <div className="p-6">
        <div className="flex gap-1 mb-4 border-b border-[#E2E4E0]">
          {[["general", "General"], ["email", "Email delivery"], ["templates", "Templates"], ["data", "Data"], ["account", "Account"]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`px-3 h-9 text-sm border-b-2 -mb-px ${tab === k ? "border-[#2D6A4F] text-[#1B4332] font-medium" : "border-transparent text-[#5C665A] hover:text-[#1A1D1A]"}`}
              data-testid={`settings-tab-${k}`}
            >{label}</button>
          ))}
        </div>
        {tab === "general" && <GeneralTab s={s} onSave={reload} />}
        {tab === "email" && <EmailTab s={s} onSave={reload} />}
        {tab === "templates" && <TemplatesTab tpls={tpls} onSave={reload} />}
        {tab === "data" && <DataTab />}
        {tab === "account" && <AccountTab />}
      </div>
    </>
  );
}

function GeneralTab({ s, onSave }) {
  const { refresh: refreshNursery } = useNurserySettings();
  const [name, setName] = useState(s.nursery_name || "");
  const [baseUrl, setBaseUrl] = useState(s.base_url || "");
  const [logo, setLogo] = useState(s.logo_storage_path || "");
  const [mode, setMode] = useState(s.stock_display_mode || "banded");
  const [bands, setBands] = useState((s.stock_bands && s.stock_bands.length ? s.stock_bands : [
    { up_to: 49, display_qty: 5 },
    { up_to: 100, display_qty: 10 },
    { up_to: 300, display_qty: 15 },
    { up_to: 500, display_qty: 20 },
  ]).map((b) => ({ ...b })));
  const [payInstructions, setPayInstructions] = useState(s.payment_instructions || "");
  const [sender, setSender] = useState(s.sender_details || "");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const uploadLogo = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/admin/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogo(data.storage_path); toast.success("Logo uploaded");
      await refreshNursery();
    } catch (err) { toast.error(getErrorMessage(err, "Failed")); }
  };

  const patchBand = (i, patch) => setBands((prev) => prev.map((b, idx) => idx === i ? { ...b, ...patch } : b));
  const addBand = () => setBands((prev) => [...prev, { up_to: (prev[prev.length - 1]?.up_to || 0) + 100, display_qty: prev[prev.length - 1]?.display_qty || 5 }]);
  const removeBand = (i) => setBands((prev) => prev.filter((_, idx) => idx !== i));

  const save = async () => {
    // Client-side validation for bands
    if (mode === "banded") {
      for (const b of bands) {
        if (!Number.isFinite(Number(b.up_to)) || Number(b.up_to) < 1) { toast.error("Band 'up to' must be at least 1"); return; }
        if (!Number.isFinite(Number(b.display_qty)) || Number(b.display_qty) < 1) { toast.error("Band 'show' must be at least 1"); return; }
      }
    }
    setBusy(true);
    try {
      await api.put("/admin/settings", {
        nursery_name: name,
        base_url: baseUrl,
        stock_display_mode: mode,
        stock_bands: bands.map((b) => ({ up_to: Number(b.up_to), display_qty: Number(b.display_qty) })),
        payment_instructions: payInstructions,
        sender_details: sender,
      });
      toast.success("Saved");
      await refreshNursery();
      onSave();
    } catch (err) { toast.error(getErrorMessage(err, "Failed")); }
    finally { setBusy(false); }
  };

  const MODES = [
    { key: "banded", label: "Banded (recommended)", desc: "Buyers see a small controlled number (e.g. \u201c15+ available\u201d). Protects exclusivity." },
    { key: "hidden", label: "Hidden", desc: "Buyers only see \u201cIn stock\u201d / \u201cSold out\u201d. No numbers." },
    { key: "exact", label: "Exact", desc: "Buyers see the real quantity. Fastest but exposes inventory." },
  ];

  return (
    <div className="max-w-2xl bg-white border border-[#E2E4E0] rounded-lg p-5" data-testid="settings-general">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-[#5C665A]">Nursery name</label>
          <Input data-testid="settings-nursery-name" value={name} onChange={(e) => setName(e.target.value)} className="h-9" />
        </div>
        <div>
          <label className="text-xs text-[#5C665A]">Logo</label>
          <div className="flex items-center gap-3">
            <div className="w-16 h-16 rounded-md bg-[#F9F9F8] border border-[#E2E4E0] overflow-hidden grid place-items-center">
              {logo ? <img src={fileUrl(logo)} alt="logo" className="w-full h-full object-contain" data-testid="settings-logo-preview" /> : <span className="text-xs text-[#5C665A]">no logo</span>}
            </div>
            <button className="text-sm inline-flex items-center gap-1 h-9 px-3 rounded-md border border-[#E2E4E0] hover:bg-[#F9F9F8]" onClick={() => fileRef.current?.click()} data-testid="settings-logo-upload"><Upload size={14} /> Upload</button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadLogo(e.target.files[0])} />
          </div>
        </div>
        <div className="md:col-span-2">
          <label className="text-xs text-[#5C665A]">Public app URL</label>
          <Input data-testid="settings-base-url" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://yourdomain.com" className="h-9" />
          <div className="text-[11px] text-[#5C665A] mt-1">Used in outbound email links (approve/reject, order, back-in-stock). Leave blank to use relative paths.</div>
        </div>
        <div className="md:col-span-2 border-t border-[#E2E4E0] pt-4">
          <label className="text-xs text-[#5C665A]">Payment instructions</label>
          <Textarea
            data-testid="settings-payment-instructions"
            value={payInstructions}
            onChange={(e) => setPayInstructions(e.target.value)}
            rows={5}
            placeholder={"Bank: ...\nIBAN: NL00 ABNA ...\nBIC: ABNANL2A\nReference: order or invoice number\nTerms: 14 days from invoice date"}
          />
          <div className="text-[11px] text-[#5C665A] mt-1">
            Rendered as its own section on the invoice PDF and in the buyer&apos;s order-confirmed email.
          </div>
        </div>
        <div className="md:col-span-2 border-t border-[#E2E4E0] pt-4">
          <label className="text-xs text-[#5C665A]">Sender details (Pro Forma header)</label>
          <Textarea
            data-testid="settings-sender-details"
            value={sender}
            onChange={(e) => setSender(e.target.value)}
            rows={7}
            placeholder={"Company name\nStreet 1, 0000 AA City\nCountry\n\nCoC: 00000000\nVAT: NL000000000B00\n\nphone: +00 000 000 000\nemail: hello@example.com\nweb: example.com"}
          />
          <div className="text-[11px] text-[#5C665A] mt-1">
            Full sender company block for the Pro Forma invoice header. Not shown on the commercial invoice.
          </div>
        </div>
        <div className="md:col-span-2 border-t border-[#E2E4E0] pt-4">
          <div className="text-sm font-medium text-[#1A1D1A]">Stock display to buyers</div>
          <div className="mt-2 space-y-2">
            {MODES.map((m) => (
              <label key={m.key} className="flex items-start gap-3 cursor-pointer p-2 rounded-md border border-[#E2E4E0] hover:bg-[#F9F9F8]" data-testid={`settings-stock-mode-${m.key}`}>
                <input
                  type="radio"
                  name="stock_display_mode"
                  checked={mode === m.key}
                  onChange={() => setMode(m.key)}
                  className="mt-1"
                  data-testid={`settings-stock-mode-radio-${m.key}`}
                />
                <span>
                  <span className="text-sm font-medium">{m.label}</span>
                  <span className="block text-[11px] text-[#5C665A] mt-0.5">{m.desc}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
        {mode === "banded" && (
          <div className="md:col-span-2 border-t border-[#E2E4E0] pt-4" data-testid="settings-band-editor">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">Display bands</div>
              <button
                type="button"
                onClick={addBand}
                className="text-xs h-8 px-2 rounded-md border border-[#E2E4E0] hover:bg-[#F9F9F8] inline-flex items-center gap-1"
                data-testid="settings-band-add"
              >+ Add row</button>
            </div>
            <div className="text-[11px] text-[#5C665A] mt-1 mb-2">
              Real stock <span className="db-mono">up to</span> the threshold → buyer sees the <span className="db-mono">show</span> value.
              Stock above the last threshold uses the last row&apos;s value. Buyer never sees more than the real stock (a size with only 3 shows 3).
            </div>
            <div className="space-y-1">
              {bands.map((b, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center" data-testid={`settings-band-row-${i}`}>
                  <div>
                    <label className="text-[10px] text-[#5C665A] uppercase">Real ≤</label>
                    <Input type="number" min={1} value={b.up_to} onChange={(e) => patchBand(i, { up_to: Number(e.target.value) || 0 })} className="h-9 db-mono text-right" data-testid={`settings-band-up-${i}`} />
                  </div>
                  <div>
                    <label className="text-[10px] text-[#5C665A] uppercase">Show</label>
                    <Input type="number" min={1} value={b.display_qty} onChange={(e) => patchBand(i, { display_qty: Number(e.target.value) || 0 })} className="h-9 db-mono text-right" data-testid={`settings-band-show-${i}`} />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeBand(i)}
                    className="h-9 w-9 text-[#5C665A] hover:text-[#C94A4A] grid place-items-center"
                    data-testid={`settings-band-remove-${i}`}
                    aria-label="Remove"
                  >×</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="flex justify-end mt-4">
        <Button data-testid="settings-general-save" disabled={busy} onClick={save} className="h-9 bg-[#2D6A4F] hover:bg-[#1B4332] text-white"><Save size={14} /> Save</Button>
      </div>
    </div>
  );
}

function EmailTab({ s, onSave }) {
  const [notif, setNotif] = useState(s.notification_email || "");
  const [domain, setDomain] = useState(s.mailgun_domain || "");
  const [sender, setSender] = useState(s.mailgun_sender || "");
  const [region, setRegion] = useState(s.mailgun_region || "");
  const [apiKey, setApiKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const configured = s.mailgun_configured;

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        notification_email: notif || null,
        mailgun_domain: domain || null,
        mailgun_sender: sender || null,
        mailgun_region: region || null,
      };
      if (apiKey.trim()) body.mailgun_api_key = apiKey.trim();
      await api.put("/admin/settings", body);
      setApiKey(""); toast.success("Saved"); onSave();
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    setTestBusy(true);
    try {
      const { data } = await api.post("/admin/settings/test-email", {});
      if (data?.status === "sent") toast.success(`Test email sent to ${data.to}`);
      else toast.error(`Test send ${data?.status || "failed"}: ${data?.error || "unknown"}`);
    } catch (err) { toast.error(getErrorMessage(err, "Test send failed")); }
    finally { setTestBusy(false); }
  };

  return (
    <div className="max-w-2xl bg-white border border-[#E2E4E0] rounded-lg p-5" data-testid="settings-email">
      <div>
        <label className="text-xs text-[#5C665A] inline-flex items-center gap-1"><Mail size={12} /> Notification email (admin)</label>
        <Input data-testid="settings-notification-email" value={notif} onChange={(e) => setNotif(e.target.value)} placeholder="ops@yourdomain.com" className="h-9" />
        <div className="text-[11px] text-[#5C665A] mt-1">
          Admin-facing emails (new orders, new registrations) go here.
          <b> If empty, we fall back to the admin login email</b> so production never silently drops.
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-[#E2E4E0]">
        <div className="flex items-center gap-2 mb-2">
          <span className="db-h font-semibold text-sm">Mailgun</span>
          {configured ? <Badge className="bg-[#E9F5EC] text-[#1B4332] border-transparent">Connected</Badge> : <Badge className="bg-neutral-100 text-neutral-600 border-transparent">Not configured</Badge>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-[#5C665A]">Domain</label>
            <Input data-testid="settings-mailgun-domain" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="mg.yourdomain.com" className="h-9" />
          </div>
          <div>
            <label className="text-xs text-[#5C665A]">Sender</label>
            <Input data-testid="settings-mailgun-sender" value={sender} onChange={(e) => setSender(e.target.value)} placeholder="noreply@yourdomain.com" className="h-9" />
          </div>
          <div>
            <label className="text-xs text-[#5C665A]">Region</label>
            <select
              className="h-9 w-full border border-[#E2E4E0] rounded-md px-2 bg-white text-sm"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              data-testid="settings-mailgun-region"
            >
              <option value="">Auto-detect (US → EU fallback)</option>
              <option value="us">US (api.mailgun.net)</option>
              <option value="eu">EU (api.eu.mailgun.net)</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-[#5C665A] inline-flex items-center gap-1"><KeyRound size={12} /> API key</label>
            <Input data-testid="settings-mailgun-key" type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder={configured ? "••••••••" + (s.mailgun_api_key_masked?.slice(-4) || "") : "key-••••"} className="h-9 db-mono" />
            <div className="text-[11px] text-[#5C665A] mt-1">Leave blank to keep the existing key. Enter a new value to replace.</div>
          </div>
        </div>
      </div>
      <div className="flex justify-between items-center mt-4">
        <Button
          variant="outline"
          onClick={sendTest}
          disabled={testBusy || !configured}
          className="h-9"
          data-testid="settings-email-test-send"
        >
          {testBusy ? "Sending…" : "Send test email"}
        </Button>
        <Button data-testid="settings-email-save" disabled={busy} onClick={save} className="h-9 bg-[#2D6A4F] hover:bg-[#1B4332] text-white"><Save size={14} /> Save</Button>
      </div>
    </div>
  );
}

function TemplatesTab({ tpls, onSave }) {
  const [selKey, setSelKey] = useState(tpls[0]?.key || "");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  const cur = tpls.find((t) => t.key === selKey);
  useEffect(() => { if (cur) { setSubject(cur.subject); setBody(cur.body); } }, [selKey, cur]);

  const save = async () => {
    try { await api.put(`/admin/email-templates/${selKey}`, { subject, body }); toast.success("Template saved"); onSave(); }
    catch { toast.error("Failed"); }
  };

  const reset = async () => {
    try {
      const { data } = await api.post(`/admin/email-templates/${selKey}/reset`);
      setSubject(data.subject); setBody(data.body);
      toast.success("Reset to default");
      onSave();
    } catch (err) { toast.error("Reset failed"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4" data-testid="settings-templates">
      <div className="bg-white border border-[#E2E4E0] rounded-lg overflow-hidden">
        {tpls.map((t) => (
          <button key={t.key} onClick={() => setSelKey(t.key)}
            className={`w-full text-left px-3 py-2 border-b border-[#E2E4E0] last:border-b-0 text-sm ${selKey === t.key ? "bg-[#E9F5EC] text-[#1B4332]" : "hover:bg-[#F9F9F8]"}`}
            data-testid={`settings-tpl-${t.key}`}
          >
            <div className="font-medium">{t.name}</div>
            <div className="text-[11px] text-[#5C665A] uppercase tracking-wider">{t.audience} · {t.key}</div>
          </button>
        ))}
      </div>
      {cur && (
        <div className="bg-white border border-[#E2E4E0] rounded-lg p-5">
          <div>
            <label className="text-xs text-[#5C665A]">Subject</label>
            <Input data-testid="settings-tpl-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="h-9" />
          </div>
          <div className="mt-3">
            <label className="text-xs text-[#5C665A]">Body</label>
            <Textarea data-testid="settings-tpl-body" value={body} onChange={(e) => setBody(e.target.value)} rows={12} className="db-mono text-xs" />
          </div>
          <div className="mt-3 text-xs text-[#5C665A]">
            <span className="font-medium">Variables:</span>{" "}
            {(cur.variables_doc || []).map((v) => <code key={v} className="db-mono bg-[#F9F9F8] rounded px-1.5 py-0.5 mr-1">{`{{ ${v} }}`}</code>)}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" data-testid="settings-tpl-reset" onClick={reset} className="h-9">Reset to default</Button>
            <Button data-testid="settings-tpl-save" onClick={save} className="h-9 bg-[#2D6A4F] hover:bg-[#1B4332] text-white"><Save size={14} /> Save template</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function DataTab() {
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  const run = async () => {
    setConfirm(false); setBusy(true);
    try {
      const { data } = await api.post("/admin/data/reimport");
      setLastRun(data);
      const steps = data.steps || {};
      const ran = Object.entries(steps).filter(([, v]) => v && v.ran).map(([k]) => k);
      if (ran.length) toast.success(`Re-import ran: ${ran.join(", ")}`);
      else toast.success("Everything already in place — nothing to import.");
    } catch (err) {
      toast.error(getErrorMessage(err, "Re-import failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-[#E2E4E0] rounded-lg p-5 max-w-3xl space-y-4" data-testid="settings-data">
      <div>
        <h3 className="db-h font-semibold inline-flex items-center gap-2"><DatabaseBackup size={16} /> Re-run data imports</h3>
        <p className="text-sm text-[#5C665A] mt-1 leading-relaxed">
          Re-runs the catalogue and parents-registry imports from the bundled
          workbooks. <b>Safe</b> — only fills EMPTY collections and never overwrites
          existing products, prices, stock, or parent photos. Use after a fresh
          deploy when a collection is unexpectedly empty.
        </p>
      </div>
      <div className="bg-[#FBF7E6] border border-[#EFE1A6] rounded-md p-3 text-xs text-[#8A5A2B]">
        What this does:
        <ul className="list-disc pl-5 mt-1 space-y-0.5">
          <li>If no admin / foundation data → seed admin + sizes + categories + tiers + countries + demo buyers</li>
          <li>If parents registry is empty → import mother-plant names from bundled workbook</li>
          <li>If products collection is empty → import Florali stock from bundled workbook</li>
          <li>Always: refresh email templates and settings (non-destructive upgrades)</li>
        </ul>
      </div>
      <div>
        <Button
          onClick={() => setConfirm(true)}
          disabled={busy}
          className="h-9 bg-[#2D6A4F] hover:bg-[#1B4332] text-white"
          data-testid="settings-data-reimport"
        >
          {busy ? <><Loader2 className="animate-spin" size={14} /> Running…</> : <><DatabaseBackup size={14} /> Re-run data imports</>}
        </Button>
      </div>

      {lastRun && (
        <div className="border border-[#E2E4E0] rounded-md p-3" data-testid="settings-data-result">
          <div className="text-xs text-[#5C665A] mb-1">Last run · source: <code className="db-mono">{lastRun.source}</code></div>
          <table className="w-full text-sm">
            <tbody>
              {Object.entries(lastRun.steps || {}).map(([k, v]) => (
                <tr key={k} className="border-t border-[#E2E4E0] first:border-t-0" data-testid={`settings-data-step-${k}`}>
                  <td className="py-1.5 pr-2 db-h font-semibold capitalize align-top w-48">{k.replace(/_/g, " ")}</td>
                  <td className="py-1.5">
                    {v && typeof v === "object" ? (
                      <StepPayload payload={v} />
                    ) : <span className="text-[#5C665A] text-xs">{String(v)}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title="Re-run data imports?"
        description="Fills any empty collection from the bundled workbooks. Non-destructive — existing products, prices, stock, and parent photos stay as-is."
        confirmLabel="Run imports"
        onConfirm={run}
        testid="settings-data-confirm"
      />
      <LaunchCleanupPanel />
    </div>
  );
}

function StepPayload({ payload }) {
  if (payload.ran === false) {
    return <span className="text-xs"><Badge className="bg-neutral-100 text-neutral-600 border-transparent mr-2">skipped</Badge>{payload.reason || "no-op"}</span>;
  }
  if (payload.ran === true) {
    const { ran, ...rest } = payload;
    const pairs = Object.entries(rest).filter(([, v]) => v !== undefined && !Array.isArray(v) && typeof v !== "object");
    return (
      <span className="text-xs">
        <Badge className="bg-[#E9F5EC] text-[#1B4332] border-transparent mr-2">ran</Badge>
        {pairs.map(([k, v]) => <span key={k} className="mr-3"><span className="text-[#5C665A]">{k}:</span> <code className="db-mono">{String(v)}</code></span>)}
      </span>
    );
  }
  // Migrations shape: { email_templates: "ok", settings: "ok" }
  return (
    <span className="text-xs">
      {Object.entries(payload).filter(([, v]) => typeof v !== "object").map(([k, v]) => <span key={k} className="mr-3"><span className="text-[#5C665A]">{k}:</span> <code className="db-mono">{String(v)}</code></span>)}
    </span>
  );
}


function AccountTab() {
  const [me, setMe] = useState(null);
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => setMe({}));
  }, []);

  const canSubmit = cur.length > 0 && next.length >= 10 && confirmPw === next && next !== cur && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password changed");
      setCur(""); setNext(""); setConfirmPw("");
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to change password"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-lg space-y-5" data-testid="settings-account">
      <div>
        <div className="db-h font-semibold text-[#1A1D1A] flex items-center gap-2"><Lock size={16} /> Change password</div>
        <p className="text-sm text-[#5C665A] mt-1 leading-relaxed">
          Changes the password for the currently signed-in account
          {me?.email ? <> — <code className="db-mono">{me.email}</code></> : null}.
          Minimum 10 characters.
        </p>
      </div>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="text-xs text-[#5C665A]">Current password</label>
          <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)}
                 autoComplete="current-password" className="h-10" data-testid="account-current-password" />
        </div>
        <div>
          <label className="text-xs text-[#5C665A]">New password <span className="text-[#8A9088]">(min 10 chars)</span></label>
          <Input type="password" value={next} onChange={(e) => setNext(e.target.value)}
                 autoComplete="new-password" className="h-10" data-testid="account-new-password" />
          {next.length > 0 && next.length < 10 && (
            <div className="text-[11px] text-[#B23A48] mt-1">Too short — at least 10 characters.</div>
          )}
          {next.length >= 10 && next === cur && (
            <div className="text-[11px] text-[#B23A48] mt-1">Must differ from the current password.</div>
          )}
        </div>
        <div>
          <label className="text-xs text-[#5C665A]">Confirm new password</label>
          <Input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
                 autoComplete="new-password" className="h-10" data-testid="account-confirm-password" />
          {confirmPw.length > 0 && confirmPw !== next && (
            <div className="text-[11px] text-[#B23A48] mt-1">Does not match the new password.</div>
          )}
        </div>
        <div className="pt-1">
          <Button type="submit" disabled={!canSubmit}
                  className="h-9 bg-[#2D6A4F] hover:bg-[#1B4332] text-white disabled:opacity-50"
                  data-testid="account-change-password-submit">
            {busy ? <><Loader2 className="animate-spin" size={14} /> Changing…</> : <>Change password</>}
          </Button>
        </div>
      </form>
    </div>
  );
}


function LaunchCleanupPanel() {
  const [buyers, setBuyers] = useState(null);
  const [keep, setKeep] = useState(() => new Set());
  const [deleteAllOrders, setDeleteAllOrders] = useState(false);
  const [clearStockLogs, setClearStockLogs] = useState(true);
  const [clearOutbox, setClearOutbox] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirmPhrase, setConfirmPhrase] = useState("");
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const reload = async () => {
    try {
      const { data } = await api.get("/admin/launch-cleanup/buyers");
      setBuyers(data.buyers || []);
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to load buyers"));
    }
  };
  useEffect(() => { reload(); }, []);

  const toggleKeep = (id) => {
    setKeep((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setPreview(null);
  };

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post("/admin/launch-cleanup/preview", {
        keep_buyer_ids: Array.from(keep),
        delete_all_orders: deleteAllOrders,
        clear_stock_logs: clearStockLogs,
        clear_outbox: clearOutbox,
        confirm: "",
      });
      setPreview(data);
    } catch (err) {
      toast.error(getErrorMessage(err, "Preview failed"));
    } finally {
      setPreviewing(false);
    }
  };

  const execute = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/launch-cleanup/execute", {
        keep_buyer_ids: Array.from(keep),
        delete_all_orders: deleteAllOrders,
        clear_stock_logs: clearStockLogs,
        clear_outbox: clearOutbox,
        confirm: confirmPhrase,
      });
      setLastResult(data);
      setConfirmPhrase("");
      setPreview(null);
      toast.success("Launch cleanup complete");
      await reload();
    } catch (err) {
      toast.error(getErrorMessage(err, "Cleanup failed"));
    } finally {
      setRunning(false);
    }
  };

  const canExecute = preview != null && confirmPhrase.trim() === "CLEANUP" && !running;

  return (
    <div className="bg-white border border-[#D33A3A]/30 rounded-lg p-5 max-w-3xl space-y-4 mt-6" data-testid="settings-launch-cleanup">
      <div className="flex items-start gap-3">
        <AlertTriangle className="text-[#D33A3A] mt-0.5" size={20} />
        <div>
          <h3 className="db-h font-semibold text-[#8A1A1A]">Danger zone · Launch cleanup</h3>
          <p className="text-sm text-[#5C665A] mt-1 leading-relaxed">
            Permanently removes test/demo data before go-live. Tick the buyers
            you want to <b>keep</b> — everything else (buyer accounts, logins,
            carts, their orders, admin notifications, notify-me requests) is
            deleted. Admins are never touched.
          </p>
        </div>
      </div>

      <div className="bg-[#FEF3E0] border border-[#EFC97A] rounded-md p-3 text-xs text-[#8A5A2B] leading-relaxed">
        <b>Stock quantities are NOT changed by this cleanup.</b> Deleting a confirmed order will not restore its stock —
        review your Stock page afterwards. This action also flips a flag so demo
        buyers never re-seed on future boots.
      </div>

      {buyers === null ? (
        <div className="text-sm text-[#5C665A]">Loading buyers…</div>
      ) : buyers.length === 0 ? (
        <div className="text-sm text-[#5C665A]">No buyers in the database.</div>
      ) : (
        <div className="border border-[#E2E4E0] rounded-md overflow-hidden" data-testid="cleanup-buyers-table">
          <table className="w-full text-sm">
            <thead className="bg-[#F3F5F2] text-[#5C665A]">
              <tr className="text-left">
                <th className="px-3 py-2 w-16">Keep</th>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Orders</th>
              </tr>
            </thead>
            <tbody>
              {buyers.map((b) => (
                <tr key={b.id} className="border-t border-[#E2E4E0]" data-testid={`cleanup-buyer-row-${b.id}`}>
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={keep.has(b.id)}
                      onChange={() => toggleKeep(b.id)}
                      data-testid={`cleanup-keep-${b.id}`}
                      className="w-4 h-4 accent-[#2D6A4F]"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-[#1A1D1A]">{b.name || "—"}</div>
                    {b.company && <div className="text-xs text-[#5C665A]">{b.company}</div>}
                  </td>
                  <td className="px-3 py-2 db-mono text-xs">{b.email}</td>
                  <td className="px-3 py-2 text-xs capitalize text-[#5C665A]">{b.status}</td>
                  <td className="px-3 py-2 text-right db-mono text-xs">{b.order_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="space-y-2 pt-1">
        <label className="flex items-start gap-2 text-sm" data-testid="cleanup-opt-delete-all-orders">
          <input type="checkbox" checked={deleteAllOrders} onChange={(e) => { setDeleteAllOrders(e.target.checked); setPreview(null); }} className="mt-1 w-4 h-4 accent-[#2D6A4F]" />
          <span>
            <b>Delete ALL orders</b> (including kept buyers' orders). Default off — kept buyers keep their order history.
          </span>
        </label>
        <label className="flex items-start gap-2 text-sm" data-testid="cleanup-opt-clear-stock-logs">
          <input type="checkbox" checked={clearStockLogs} onChange={(e) => { setClearStockLogs(e.target.checked); setPreview(null); }} className="mt-1 w-4 h-4 accent-[#2D6A4F]" />
          <span>
            <b>Clear stock logs before now</b> (fresh audit trail from today). Default on.
          </span>
        </label>
        <label className="flex items-start gap-2 text-sm" data-testid="cleanup-opt-clear-outbox">
          <input type="checkbox" checked={clearOutbox} onChange={(e) => { setClearOutbox(e.target.checked); setPreview(null); }} className="mt-1 w-4 h-4 accent-[#2D6A4F]" />
          <span>
            <b>Clear email outbox history.</b> Default off.
          </span>
        </label>
      </div>

      <div className="flex gap-2 flex-wrap pt-1">
        <Button
          onClick={runPreview}
          disabled={previewing || buyers === null}
          variant="outline"
          className="h-9 border-[#D33A3A] text-[#8A1A1A] hover:bg-[#FDECEC]"
          data-testid="cleanup-preview-btn"
        >
          {previewing ? <><Loader2 className="animate-spin" size={14} /> Previewing…</> : <>Preview impact</>}
        </Button>
      </div>

      {preview && (
        <div className="border border-[#D33A3A]/40 rounded-md p-3 bg-[#FDF4F4]" data-testid="cleanup-preview">
          <div className="text-xs uppercase tracking-wider text-[#8A1A1A] mb-2">Pre-run summary</div>
          <table className="w-full text-sm">
            <tbody>
              {Object.entries(preview.counts || {}).map(([k, v]) => (
                <tr key={k} className="border-t border-[#F0D5D5] first:border-t-0" data-testid={`cleanup-count-${k}`}>
                  <td className="py-1 pr-2 capitalize text-[#5C665A]">{k.replace(/_/g, " ")}</td>
                  <td className="py-1 db-mono text-right w-24">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(preview.buyers_to_delete || []).length > 0 && (
            <details className="mt-2 text-xs">
              <summary className="cursor-pointer text-[#8A1A1A]">{preview.buyers_to_delete.length} buyers will be deleted</summary>
              <ul className="list-disc pl-5 mt-1 space-y-0.5 text-[#5C665A]">
                {preview.buyers_to_delete.map((b) => (
                  <li key={b.id}><span className="db-mono">{b.email}</span> — {b.name} ({b.order_count} orders)</li>
                ))}
              </ul>
            </details>
          )}
          <div className="mt-3 border-t border-[#F0D5D5] pt-3 space-y-2">
            <label className="text-xs text-[#8A1A1A]">To proceed, type <code className="db-mono bg-white px-1 py-0.5 rounded">CLEANUP</code>:</label>
            <Input
              value={confirmPhrase}
              onChange={(e) => setConfirmPhrase(e.target.value)}
              className="h-9 db-mono"
              placeholder="CLEANUP"
              data-testid="cleanup-confirm-input"
            />
            <Button
              onClick={execute}
              disabled={!canExecute}
              className="h-9 bg-[#D33A3A] hover:bg-[#B82B2B] text-white disabled:opacity-50"
              data-testid="cleanup-execute-btn"
            >
              {running ? <><Loader2 className="animate-spin" size={14} /> Running…</> : <><Trash2 size={14} /> Run cleanup</>}
            </Button>
          </div>
        </div>
      )}

      {lastResult && (
        <div className="border border-[#B7D5C0] rounded-md p-3 bg-[#F0F8F2]" data-testid="cleanup-last-result">
          <div className="text-xs text-[#1B4332] font-semibold mb-1">Last cleanup · {lastResult.audit?.ts}</div>
          <table className="w-full text-sm">
            <tbody>
              {Object.entries(lastResult.counts || {}).map(([k, v]) => (
                <tr key={k} className="border-t border-[#CFE2D4] first:border-t-0">
                  <td className="py-1 pr-2 capitalize text-[#5C665A]">{k.replace(/_/g, " ")}</td>
                  <td className="py-1 db-mono text-right w-24">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
